import { useEffect, useState } from "react";
import { Button, Form, Spinner } from 'react-bootstrap';

import { Validators } from "../../../shared/utils/Validators";
import { TextAreaInput, TextInput, SelectInput } from "../../../shared/form/FormInput";
import { planDetail } from "../../../shared/settings/apiConfig";
import { getApi, putApi } from "../../../shared/services/apiHelper";
import { createPlan, updatePlan } from "../../../shared/services/adminApi";

import '../clients/ClientAddEdit.scss';
import { PlusIcon } from "../../../shared/icons/Icons";

export const PlanAddEdit = (props) => {

    const isEdit = props.location.pathname.includes('edit');
    const planId = props.location.pathname.includes('add') ? '' : props?.match?.params?.id;

    const [initialLoading, setInitialLoading] = useState(isEdit);
    const [planForm, setPlanForm] = useState({
        name: { value: '', valid: false, error: '', validators: [Validators.required()] },
        code: { value: '', valid: false, error: '', validators: [Validators.required()] },
        summary: { value: '', valid: false, error: '', validators: [Validators.required()] },
        description: { value: '', valid: true, error: '', validators: [] },
        display_order: { value: '1', valid: true, error: '', validators: [] },
        is_active: { value: true, valid: true, error: '', validators: [] },
        features: { value: '', valid: true, error: '', validators: [] },
        // Price fields
        price_currency: { value: 'INR', valid: true, error: '', validators: [] },
        price_amount: { value: '0', valid: true, error: '', validators: [] },
        price_billing_cycle: { value: 'FREE', valid: true, error: '', validators: [] },
        // Limits fields
        limits_estimations: { value: '', valid: true, error: '', validators: [] },
        limits_max_floor: { value: '', valid: true, error: '', validators: [] },
        limits_building_types: { value: '', valid: true, error: '', validators: [] },
    });

    const [formLoading, setFormLoading] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const billingCycleOptions = [
        { value: 'FREE', option: 'Free' },
        { value: 'PER_BUILDING', option: 'Per Building' },
        { value: 'MONTHLY', option: 'Monthly' },
        { value: 'YEARLY', option: 'Yearly' },
        { value: 'CUSTOM', option: 'Custom' },
    ];

    const buildingTypeOptions = [
        { value: 'INDEPENDENT', option: 'Independent' },
        { value: 'APARTMENT', option: 'Apartment' },
        { value: 'COMMERCIAL', option: 'Commercial' },
        { value: 'INDUSTRIAL', option: 'Industrial' },
    ];

    useEffect(() => {
        if (planId && isEdit) {
            setInitialLoading(true);
            getApi(`${planDetail}/${planId}`).then((response) => {
                let data = response.data;
                // Map nested fields back to flat form fields
                let formData = {
                    ...data,
                    price_currency: data.price?.currency || 'INR',
                    price_amount: data.price?.amount?.toString() || '0',
                    price_billing_cycle: data.price?.billing_cycle || 'FREE',
                    limits_estimations: data.limits?.estimations?.toString() || '',
                    limits_max_floor: data.limits?.max_floor?.toString() || '',
                    limits_building_types: Array.isArray(data.limits?.building_types) ? data.limits.building_types.join(', ') : '',
                    display_order: data.display_order?.toString() || '1',
                    features: Array.isArray(data.features) ? data.features.join('\n') : '',
                };
                setPlanForm(prevState => {
                    return Validators.validateAndSetAllFormControls(prevState, formData)
                })
                setInitialLoading(false);
            }).catch((error) => {
                console.log(error);
                setInitialLoading(false);
            });
        }
    }, [planId, isEdit]);

    useEffect(() => {
        setFormValid(Validators.validateForm(planForm));
    }, [planForm]);

    const inputChange = (type, event) => {
        let value = event.target.value;
        // Handle checkbox
        if (event.target.type === 'checkbox') {
            value = event.target.checked;
        }
        setPlanForm(prevState => {
            return {
                ...prevState,
                [type]: Validators.validateFormControl(prevState[type], value)
            }
        })
    };

    const onCancel = () => {
        props.history.goBack();
    }

    const buildPayload = () => {
        let data = {
            "name": planForm.name.value,
            "code": planForm.code.value,
            "summary": planForm.summary.value,
            "description": planForm.description.value,
            "display_order": parseInt(planForm.display_order.value) || 1,
            "is_active": planForm.is_active.value,
            "features": planForm.features.value ? planForm.features.value.split('\n').filter(f => f.trim()) : [],
            "price": {
                "currency": planForm.price_currency.value || 'INR',
                "amount": parseFloat(planForm.price_amount.value) || 0,
                "billing_cycle": planForm.price_billing_cycle.value || 'FREE',
            },
            "limits": {
                "estimations": planForm.limits_estimations.value ? parseInt(planForm.limits_estimations.value) : null,
                "max_floor": planForm.limits_max_floor.value ? parseInt(planForm.limits_max_floor.value) : null,
                "building_types": planForm.limits_building_types.value
                    ? planForm.limits_building_types.value.split(',').map(t => t.trim()).filter(t => t)
                    : [],
            }
        };

        return data;
    };

    const onSavePlan = () => {
        setFormLoading(true);
        let data = buildPayload();

        if (isEdit) {
            data["id"] = planId;
            updatePlan(planId, data).then((response) => {
                if (response.data?.updated || response.status === 200) {
                    props.history.goBack();
                }
                setFormLoading(false);
            }).catch((error) => {
                setFormLoading(false);
                setErrorMessage(error.response?.data?.detail || "Error updating plan");
                setTimeout(() => {
                    setErrorMessage('');
                }, 3000);
            })
        } else {
            createPlan(data).then((response) => {
                if (response.status === 200) {
                    props.history.goBack();
                }
                setFormLoading(false);
            }).catch((error) => {
                setFormLoading(false);
                setErrorMessage(error.response?.data?.detail || "Error creating plan");
                setTimeout(() => {
                    setErrorMessage('');
                }, 3000);
            })
        }
    }

    return (
        <div className="main-content client-box w-100">
            <div className="d-flex flex-column gap-2">
                <h4 className="d-flex fw-bold gap-3">{isEdit ? "Edit Plan" : "Add New Plan"}</h4>
                <p className='content-sub-label'>Enter plan details to {isEdit ? "update" : "create"} a subscription plan</p>
            </div>
            {
                initialLoading ? (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
                        <img className='loader-icon' src={`${process.env.PUBLIC_URL}/static/images/loader.svg`} alt="loader-icon" />
                    </div>) : (
                    <>
                        <Form className="d-flex flex-column justify-content-center my-3 gap-3">
                            <div className="d-flex gap-3">
                                <TextInput type={'text'} name={'name'} label={'Plan Name'} placeholder={'Enter Plan Name'}
                                    value={planForm.name.value} error={planForm.name.error} onChange={(event) => inputChange("name", event)} isMandatory={true} />

                                <TextInput type={'text'} name={'code'} label={'Plan Code'} placeholder={'e.g. BASIC, PREMIUM'}
                                    value={planForm.code.value} error={planForm.code.error} onChange={(event) => inputChange("code", event)} isMandatory={true} />

                                <TextInput type={'number'} name={'display_order'} label={'Display Order'} placeholder={'Display order'}
                                    value={planForm.display_order.value} error={planForm.display_order.error} onChange={(event) => inputChange("display_order", event)} />
                            </div>

                            <TextInput type={'text'} name={'summary'} label={'Summary'} placeholder={'Brief summary of the plan'}
                                value={planForm.summary.value} error={planForm.summary.error} onChange={(event) => inputChange("summary", event)} isMandatory={true} />

                            <TextAreaInput name={'description'} label={'Description'} placeholder={'Detailed description of the plan...'} style={{ "minHeight": "5rem" }}
                                value={planForm.description.value} error={planForm.description.error} onChange={(event) => inputChange("description", event)} />

                            <hr />
                            <h6 className="fw-bold">Pricing</h6>
                            <div className="d-flex gap-3">
                                <SelectInput name={'price_billing_cycle'} label={'Billing Cycle'} placeholder={'Select billing cycle'}
                                    value={planForm.price_billing_cycle.value} error={planForm.price_billing_cycle.error}
                                    options={billingCycleOptions} onChange={(event) => inputChange("price_billing_cycle", event)} />

                                <TextInput type={'number'} name={'price_amount'} label={'Amount'} placeholder={'0'}
                                    value={planForm.price_amount.value} error={planForm.price_amount.error} onChange={(event) => inputChange("price_amount", event)} />

                                <TextInput type={'text'} name={'price_currency'} label={'Currency'} placeholder={'INR'}
                                    value={planForm.price_currency.value} error={planForm.price_currency.error} onChange={(event) => inputChange("price_currency", event)} />
                            </div>

                            <hr />
                            <h6 className="fw-bold">Limits</h6>
                            <div className="d-flex gap-3">
                                <TextInput type={'number'} name={'limits_estimations'} label={'Max Estimations'} placeholder={'Leave empty for unlimited'}
                                    value={planForm.limits_estimations.value} error={planForm.limits_estimations.error} onChange={(event) => inputChange("limits_estimations", event)} />

                                <TextInput type={'number'} name={'limits_max_floor'} label={'Max Floor'} placeholder={'Leave empty for unlimited'}
                                    value={planForm.limits_max_floor.value} error={planForm.limits_max_floor.error} onChange={(event) => inputChange("limits_max_floor", event)} />

                                <TextInput type={'text'} name={'limits_building_types'} label={'Building Types (comma separated)'} placeholder={'e.g. INDEPENDENT, APARTMENT'}
                                    value={planForm.limits_building_types.value} error={planForm.limits_building_types.error} onChange={(event) => inputChange("limits_building_types", event)} />
                            </div>

                            <hr />
                            <h6 className="fw-bold">Features</h6>
                            <TextAreaInput name={'features'} label={'Features (one per line)'} placeholder={'Enter each feature on a new line...'} style={{ "minHeight": "5rem" }}
                                value={planForm.features.value} error={planForm.features.error} onChange={(event) => inputChange("features", event)} />

                            <div className="d-flex align-items-center gap-2 mt-2">
                                <Form.Check
                                    type="switch"
                                    id="is_active"
                                    label="Active"
                                    checked={planForm.is_active.value}
                                    onChange={(event) => inputChange("is_active", event)}
                                />
                            </div>
                        </Form>

                        {errorMessage && (
                            <div className="d-flex align-items-center justify-content-center text-danger">{errorMessage}</div>
                        )}

                        <div className="d-flex align-items-center justify-content-center mt-5" style={{ gap: '10px' }}>
                            <Button className="btn-primary mt-4 rounded px-4 w-auto" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button className="primary-gradient-btn mt-4 rounded px-4 w-auto" onClick={onSavePlan} disabled={!formValid}>
                                <PlusIcon />{isEdit ? "Update Plan" : "Add Plan"} {formLoading ? <Spinner className='ms-2' as="span" animation="border" role="status" aria-hidden="true" /> : ''}
                            </Button>
                        </div>
                    </>
                )
            }
        </div>
    )
}